import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Icon } from '@openedx/paragon';
import {
  RiTimerLine, RiStarFill, RiArrowRightSLine, RiImageLine,
} from '@remixicon/react';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useCourseById } from '../../../gql';
import { useCourseTagsBatch } from '../../../gql/hooks/useCourseTagsBatch';
import { useContentRatings } from '../../../gql/hooks/useContentRatings';
import { useCoursesEffort } from '../useCoursesEffort';
import { formatDuration } from '../courseDataParser';
import messages from './messages';

import './DiscoverBanner.scss';

const DiscoverBannerCard = ({ banner, isHalf }) => {
  const intl = useIntl();
  const { course } = useCourseById(banner.contentId);

  const openedxCourseId = course?.openedxCourseId;
  const details = course?.details || {};
  const graphqlTags = (course?.courseTags || []).map((t) => t.tag).filter(Boolean);

  const courseIds = useMemo(
    () => (openedxCourseId ? [openedxCourseId] : []),
    [openedxCourseId],
  );
  const contentIds = useMemo(
    () => (banner.contentId ? [banner.contentId] : []),
    [banner.contentId],
  );

  const { tagsByCourseId } = useCourseTagsBatch(courseIds);
  const { ratingsByContentId } = useContentRatings(contentIds);
  const { effortByCourseId } = useCoursesEffort(courseIds);

  const taxonomyTags = openedxCourseId ? (tagsByCourseId[openedxCourseId] || []) : [];
  const skills = taxonomyTags.length > 0 ? taxonomyTags : graphqlTags;

  const ratingData = ratingsByContentId[banner.contentId];
  const rating = ratingData?.avgRating != null && ratingData.reviewCount > 0
    ? Number(Number(ratingData.avgRating).toFixed(1))
    : null;
  const reviewCount = ratingData?.reviewCount || 0;

  const totalMinutes = openedxCourseId ? effortByCourseId[openedxCourseId]?.total_minutes : null;
  const duration = formatDuration(totalMinutes, intl, messages);

  const title = details.displayName || '';
  const imageUrl = banner.imageUrl
    || (details.courseImageUrl ? `${getConfig().LMS_BASE_URL}${details.courseImageUrl}` : null);

  if (!course) return null;

  return (
    <div
      className={`discover-banner__card ${isHalf ? 'discover-banner__card--half' : ''}`}
      style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
    >
      <div className="discover-banner__gradient-left" />
      <div className="discover-banner__gradient-bottom" />

      {banner.badge && (
        <Badge
          className="discover-banner__badge"
          variant="soft"
          color="primary"
          size="sm"
        >
          {banner.badge}
        </Badge>
      )}

      <div className="discover-banner__content">
        {title && <h3 className="discover-banner__title">{title}</h3>}

        <div className="discover-banner__details">
          {skills.length > 0 && (
            <div className="discover-banner__skills">
              {skills.slice(0, 5).map((skill) => (
                <Badge key={skill} size="sm" className="discover-banner__skill-badge">{skill}</Badge>
              ))}
              {skills.length > 5 && (
                <Badge size="sm" className="discover-banner__skill-badge">
                  {intl.formatMessage(messages.extraSkillsCount, { count: skills.length - 5 })}
                </Badge>
              )}
            </div>
          )}

          <div className="discover-banner__meta">
            {duration && (
              <span className="discover-banner__meta-item">
                <Icon src={RiTimerLine} size="xs" />
                {duration}
              </span>
            )}
            {duration && rating != null && (
              <span className="discover-banner__meta-dot" />
            )}
            {rating != null && (
              <span className="discover-banner__meta-item">
                <Icon src={RiStarFill} size="xs" />
                {intl.formatMessage(messages.ratingReviews, { rating, count: reviewCount })}
              </span>
            )}
          </div>
        </div>
      </div>

      {openedxCourseId && (
        <Link
          to={`/discover/${encodeURIComponent(openedxCourseId)}`}
          className="discover-banner__learn-more"
        >
          {intl.formatMessage(messages.learnMore)}
          <Icon src={RiArrowRightSLine} size="xs" />
        </Link>
      )}
    </div>
  );
};

const DEFAULT_MARKETING_BG = '#1C1C1C';

const MarketingBannerCard = ({ banner }) => {
  const bgColor = banner.backgroundColor && banner.backgroundColor.startsWith('#')
    ? banner.backgroundColor
    : DEFAULT_MARKETING_BG;

  return (
    <div
      className="discover-banner__marketing"
      style={{ backgroundColor: bgColor }}
    >
      <div className="discover-banner__marketing-body">
        {banner.title && <h3 className="discover-banner__marketing-title">{banner.title}</h3>}
        {banner.description && <p className="discover-banner__marketing-description">{banner.description}</p>}
      </div>
      <div className="discover-banner__marketing-media" aria-hidden="true">
        {banner.imageUrl ? (
          <img src={banner.imageUrl} alt="" loading="lazy" decoding="async" />
        ) : (
          <RiImageLine size={64} className="discover-banner__marketing-icon" />
        )}
      </div>
    </div>
  );
};

const DiscoverBanner = ({ banners }) => {
  const discoverBanners = useMemo(() => {
    const all = banners || [];
    const marketing = all.find((b) => b.type === 'marketing');
    if (marketing) return { type: 'marketing', items: [marketing] };
    const featured = all.filter((b) => b.type === 'featured').slice(0, 2);
    if (featured.length > 0) return { type: 'featured', items: featured };
    return null;
  }, [banners]);

  if (!discoverBanners) return null;

  if (discoverBanners.type === 'marketing') {
    return (
      <div className="discover-banner">
        <MarketingBannerCard banner={discoverBanners.items[0]} />
      </div>
    );
  }

  const isHalf = discoverBanners.items.length === 2;

  return (
    <div className="discover-banner">
      {discoverBanners.items.map((banner) => (
        <DiscoverBannerCard key={banner.id} banner={banner} isHalf={isHalf} />
      ))}
    </div>
  );
};

export default DiscoverBanner;
